import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { PageTransition, FadeIn, StaggerContainer, StaggerItem } from '../components/layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ChevronRight, Plus, Pencil, Trash2, Loader2, Euro, Clock, GripVertical, Image, Tag, Percent, FolderTree } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { toAbsoluteUrl } from '../lib/utils';
import ImageCropUploadDialog from '../components/admin/ImageCropUploadDialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { value: 'onglerie', label: 'Onglerie' },
  { value: 'pedicure', label: 'Pédicure' },
  { value: 'bien_etre', label: 'Bien-être' }
];

const emptyService = {
  name: '',
  description: '',
  short_description: '',
  category: '',
  subcategory_id: '',
  price: '',
  duration_minutes: '',
  image_url: '',
  no_photo: false,
  is_promo: false,
  promo_type: '',
  promo_value: '',
  is_active: true
};

export default function AdminPrestationsPage() {
  const auth = useAuth();
  const { token } = auth;
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const handleAuthExpired = () => {
    toast.error('Session expirée, reconnecte-toi');
    if (typeof auth?.logout === 'function') {
      auth.logout();
    } else {
      try {
        localStorage.removeItem('token');
      } catch (_) {}
      window.location.href = '/login';
    }
  };

  const isAuthError = (err) => err?.response?.status === 401;
  const [services, setServices] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('onglerie');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState(emptyService);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [pendingImageFile, setPendingImageFile] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!token) return;
    fetchServices();
    fetchSubcategories();
  }, [token]);

  const fetchServices = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/services?include_inactive=true`, { headers });
      const raw = Array.isArray(response.data) ? response.data : [];
      const normalized = raw.map(s => ({
        ...s,
        category: s.category === 'madero' ? 'bien_etre' : s.category
      }));
      setServices(normalized);
    } catch (err) {
      console.error('Error fetching services:', err);
      if (isAuthError(err)) return handleAuthExpired();
      toast.error('Erreur lors du chargement des prestations');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSubcategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/subcategories?include_inactive=true`, { headers });
      setSubcategories(response.data.subcategories || []);
    } catch (err) {
      console.error('Error fetching subcategories:', err);
      if (isAuthError(err)) return handleAuthExpired();
    }
  };

  const getSubcategoriesForCategory = (category) => {
    return subcategories.filter(s => s.parent_category === category && s.is_active);
  };

  const getServicesByCategory = (category) => {
    const categoriesToInclude = [category];
    return services
      .filter(s => categoriesToInclude.includes(s.category))
      .sort((a, b) => a.sort_order - b.sort_order);
  };

  const openCreateDialog = () => {
    setEditingService(null);
    setFormData({ ...emptyService, category: activeCategory });
    setErrors({});
    setIsDialogOpen(true);
  };

  const openEditDialog = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      description: service.description,
      short_description: service.short_description || '',
      category: service.category,
      subcategory_id: service.subcategory_id || '',
      price: service.price.toString(),
      duration_minutes: service.duration_minutes?.toString() || '',
      image_url: service.image_url || '',
      no_photo: service.no_photo || false,
      is_promo: service.is_promo || false,
      promo_type: service.promo_type || '',
      promo_value: service.promo_value?.toString() || '',
      is_active: service.is_active
    });
    setErrors({});
    setIsDialogOpen(true);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Nom requis';
    if (!formData.description.trim()) newErrors.description = 'Description requise';
    if (!formData.short_description.trim()) newErrors.short_description = 'Description courte requise';
    if (!formData.category) newErrors.category = 'Catégorie requise';
    if (!formData.price || parseFloat(formData.price) <= 0) newErrors.price = 'Prix valide requis';
    
    // Promo validation
    if (formData.is_promo) {
      if (!formData.promo_type) newErrors.promo_type = 'Type de promo requis';
      if (!formData.promo_value || parseFloat(formData.promo_value) <= 0) {
        newErrors.promo_value = 'Valeur de promo requise';
      }
      // Validate percent is <= 100
      if (formData.promo_type === 'percent' && parseFloat(formData.promo_value) > 100) {
        newErrors.promo_value = 'Le pourcentage ne peut dépasser 100%';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);

    const payload = {
      name: formData.name,
      description: formData.description,
      short_description: formData.short_description,
      category: formData.category,
      subcategory_id: formData.subcategory_id || null,
      price: parseFloat(formData.price),
      duration_minutes: formData.duration_minutes ? parseInt(formData.duration_minutes) : null,
      image_url: formData.image_url || null,
      no_photo: formData.no_photo,
      is_promo: formData.is_promo,
      promo_type: formData.is_promo ? formData.promo_type : null,
      promo_value: formData.is_promo ? parseFloat(formData.promo_value) : null,
      is_active: formData.is_active
    };

    try {
      if (editingService) {
        await axios.put(`${API_URL}/admin/services/${editingService.id}`, payload, { headers });
        toast.success('Prestation mise à jour');
      } else {
        await axios.post(`${API_URL}/admin/services`, payload, { headers });
        toast.success('Prestation créée');
      }
      setIsDialogOpen(false);
      fetchServices();
    } catch (err) {
      console.error('Error saving service:', err);
      if (isAuthError(err)) return handleAuthExpired();
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleActive = async (service) => {
    try {
      await axios.patch(`${API_URL}/admin/services/${service.id}/toggle`, {}, { headers });
      setServices(prev => prev.map(s => 
        s.id === service.id ? { ...s, is_active: !s.is_active } : s
      ));
      toast.success(service.is_active ? 'Prestation désactivée' : 'Prestation activée');
    } catch (err) {
      console.error('Error toggling service:', err);
      if (isAuthError(err)) return handleAuthExpired();
      toast.error('Erreur lors de la modification');
    }
  };

  const deleteService = async (service) => {
    if (!window.confirm(`Supprimer "${service.name}" ?`)) return;

    try {
      await axios.delete(`${API_URL}/admin/services/${service.id}`, { headers });
      setServices(prev => prev.filter(s => s.id !== service.id));
      toast.success('Prestation supprimée');
    } catch (err) {
      console.error('Error deleting service:', err);
      if (isAuthError(err)) return handleAuthExpired();
      toast.error('Erreur lors de la suppression');
    }
  };

  const calculatePromoPrice = (price, promoType, promoValue) => {
    if (!promoType || !promoValue) return null;
    if (promoType === 'amount') {
      return Math.max(0, price - parseFloat(promoValue));
    }
    return price * (1 - parseFloat(promoValue) / 100);
  };

  const uploadServiceImage = async (file) => {
    const payload = new FormData();
    payload.append('file', file);

    setIsUploadingImage(true);
    try {
      const response = await axios.post(`${API_URL}/uploads/service-image`, payload, { headers });
      if (response.data?.image_url) {
        setFormData(prev => ({ ...prev, image_url: response.data.image_url, no_photo: false }));
        toast.success('Image uploadée');
      } else {
        toast.error('Réponse upload invalide');
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      if (isAuthError(err)) return handleAuthExpired();
      toast.error('Erreur lors de l\'upload de l\'image');
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleImageSelection = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setPendingImageFile(file);
  };

  const handleCroppedImageConfirm = async (croppedFile) => {
    await uploadServiceImage(croppedFile);
    setPendingImageFile(null);
  };

  return (
    <PageTransition>
      {/* Hero */}
      <section className="pt-32 pb-8 px-6 bg-[var(--charcoal)]" data-testid="admin-prestations-hero">
        <div className="max-w-[800px] mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light mb-4 text-white"
          >
            Gestion des Prestations
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-white/60"
          >
            Créer, modifier et organiser vos prestations
          </motion.p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-white py-4 border-b border-[var(--champagne)]">
        <div className="max-w-[1400px] mx-auto px-6">
          <nav className="flex items-center gap-2 text-sm text-[var(--charcoal)]/60">
            <Link to="/" className="hover:text-[var(--gold)]">Accueil</Link>
            <ChevronRight size={14} />
            <Link to="/admin" className="hover:text-[var(--gold)]">Admin</Link>
            <ChevronRight size={14} />
            <span className="text-[var(--charcoal)]">Prestations</span>
          </nav>
        </div>
      </div>

      {/* Content */}
      <section className="py-12 px-6" data-testid="admin-prestations-content">
        <div className="max-w-[1200px] mx-auto">
          {/* Header Actions */}
          <div className="flex justify-between items-center mb-8">
            <h2 className="font-['Cormorant_Garamond'] text-2xl text-[var(--charcoal)]">
              Toutes les prestations
            </h2>
            <Button
              onClick={openCreateDialog}
              className="bg-[var(--gold)] text-white hover:bg-[var(--dark-gold)] rounded-none"
              data-testid="btn-add-service"
            >
              <Plus size={16} className="mr-2" />
              Nouvelle prestation
            </Button>
          </div>

          {/* Category Tabs */}
          <Tabs value={activeCategory} onValueChange={setActiveCategory}>
            <TabsList className="w-full flex justify-start mb-8 bg-transparent gap-2 border-b border-[var(--champagne)] pb-4 flex-wrap">
              {categories.map(cat => (
                <TabsTrigger
                  key={cat.value}
                  value={cat.value}
                  data-testid={`tab-${cat.value}`}
                  className="px-6 py-3 text-xs uppercase tracking-[0.15em] font-medium data-[state=active]:bg-[var(--charcoal)] data-[state=active]:text-white rounded-none transition-all"
                >
                  {cat.label} ({getServicesByCategory(cat.value).length})
                </TabsTrigger>
              ))}
            </TabsList>

            {categories.map(cat => (
              <TabsContent key={cat.value} value={cat.value}>
                <FadeIn>
                  {isLoading ? (
                    <div className="text-center py-12">
                      <Loader2 size={32} className="animate-spin mx-auto mb-4 text-[var(--gold)]" />
                      <p className="text-sm text-[var(--charcoal)]/60">Chargement...</p>
                    </div>
                  ) : getServicesByCategory(cat.value).length === 0 ? (
                    <div className="text-center py-12 bg-white border border-[var(--champagne)]">
                      <p className="text-[var(--charcoal)]/60 mb-4">
                        Aucune prestation dans cette catégorie
                      </p>
                      <Button
                        onClick={openCreateDialog}
                        variant="outline"
                        className="rounded-none"
                      >
                        <Plus size={16} className="mr-2" />
                        Créer une prestation
                      </Button>
                    </div>
                  ) : (
                    <StaggerContainer className="space-y-3">
                      {getServicesByCategory(cat.value).map((service, index) => (
                        <StaggerItem key={service.id}>
                          <div
                            className={`bg-white p-6 border transition-all ${
                              service.is_active 
                                ? 'border-[var(--champagne)] hover:border-[var(--gold)]/30' 
                                : 'border-red-200 bg-red-50/30'
                            }`}
                            data-testid={`service-row-${index}`}
                          >
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                              {/* Drag Handle & Info */}
                              <div className="flex items-start gap-4 flex-1">
                                <GripVertical size={20} className="text-[var(--charcoal)]/20 mt-1 cursor-grab hidden md:block" />
                                
                                {/* Photo Preview */}
                                <div className="w-16 h-16 bg-[var(--champagne)]/30 flex items-center justify-center shrink-0">
                                  {service.no_photo ? (
                                    <span className="text-[var(--gold)] font-['Cormorant_Garamond'] text-lg">DSRS</span>
                                  ) : service.image_url ? (
                                    <img src={toAbsoluteUrl(service.image_url)} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    <Image size={20} className="text-[var(--charcoal)]/20" />
                                  )}
                                </div>
                                
                                <div className="flex-1">
                                  <div className="flex items-center gap-3 mb-1 flex-wrap">
                                    <h3 className="font-medium text-[var(--charcoal)]">
                                      {service.name}
                                    </h3>
                                    {!service.is_active && (
                                      <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded">
                                        Inactif
                                      </span>
                                    )}
                                    {service.is_promo && (
                                      <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded flex items-center gap-1">
                                        <Tag size={10} />
                                        Promo {service.promo_type === 'percent' ? `${service.promo_value}%` : `-${service.promo_value}€`}
                                      </span>
                                    )}
                                    {service.subcategory_name && (
                                      <span className="px-2 py-0.5 text-xs bg-teal-100 text-teal-700 rounded flex items-center gap-1">
                                        <FolderTree size={10} />
                                        {service.subcategory_name}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-sm text-[var(--charcoal)]/60 line-clamp-2">
                                    {service.description}
                                  </p>
                                </div>
                              </div>

                              {/* Price & Duration */}
                              <div className="flex items-center gap-6 text-sm">
                                {service.duration_minutes && (
                                  <div className="flex items-center gap-1 text-[var(--charcoal)]/60">
                                    <Clock size={14} />
                                    <span>{service.duration_minutes}min</span>
                                  </div>
                                )}
                                <div className="flex flex-col items-end min-w-[80px]">
                                  {service.is_promo && service.promo_price ? (
                                    <>
                                      <span className="text-[var(--charcoal)]/40 line-through text-xs">{service.price}€</span>
                                      <span className="text-green-600 font-medium">{service.promo_price.toFixed(2)}€</span>
                                    </>
                                  ) : (
                                    <span className="text-[var(--gold)] font-medium">{service.price}€</span>
                                  )}
                                </div>
                              </div>

                              {/* Actions */}
                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={service.is_active}
                                  onCheckedChange={() => toggleActive(service)}
                                  data-testid={`toggle-${service.id}`}
                                />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditDialog(service)}
                                  data-testid={`edit-${service.id}`}
                                >
                                  <Pencil size={16} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => deleteService(service)}
                                  className="text-red-500 hover:text-red-700"
                                  data-testid={`delete-${service.id}`}
                                >
                                  <Trash2 size={16} />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </StaggerItem>
                      ))}
                    </StaggerContainer>
                  )}
                </FadeIn>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Cormorant_Garamond'] text-2xl">
              {editingService ? 'Modifier la prestation' : 'Nouvelle prestation'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {/* Basic Info */}
            <div>
              <Label>Nom <span className="text-red-500">*</span></Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ex: Pose complète gel"
                className={errors.name ? 'border-red-500' : ''}
                data-testid="input-service-name"
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>

            <div>
              <Label>Description <span className="text-red-500">*</span></Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Description complète de la prestation..."
                className={`min-h-[80px] ${errors.description ? 'border-red-500' : ''}`}
                data-testid="input-service-description"
              />
              {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
            </div>

            <div>
              <Label>Description courte <span className="text-red-500">*</span></Label>
              <Textarea
                value={formData.short_description}
                onChange={(e) => setFormData(prev => ({ ...prev, short_description: e.target.value }))}
                placeholder="Résumé court pour le bloc Prestations Signature (1-2 lignes)"
                className={`min-h-[60px] ${errors.short_description ? 'border-red-500' : ''}`}
                data-testid="input-service-short-description"
              />
              {errors.short_description && <p className="text-red-500 text-xs mt-1">{errors.short_description}</p>}
              <p className="text-xs text-[var(--charcoal)]/50 mt-1">
                Utilisée uniquement dans le bloc &quot;Prestations Signature&quot;
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Catégorie <span className="text-red-500">*</span></Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, category: value, subcategory_id: '' }))}
                >
                  <SelectTrigger className={errors.category ? 'border-red-500' : ''} data-testid="select-service-category">
                    <SelectValue placeholder="Choisir" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.category && <p className="text-red-500 text-xs mt-1">{errors.category}</p>}
              </div>

              <div>
                <Label>Sous-catégorie</Label>
                <Select
                  value={formData.subcategory_id || "none"}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, subcategory_id: value === "none" ? "" : value }))}
                  disabled={!formData.category}
                >
                  <SelectTrigger data-testid="select-service-subcategory">
                    <SelectValue placeholder="Aucune" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucune</SelectItem>
                    {getSubcategoriesForCategory(formData.category).map(sub => (
                      <SelectItem key={sub.id} value={sub.id}>{sub.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-[var(--charcoal)]/50 mt-1">
                  <Link to="/admin/subcategories" className="text-[var(--gold)] hover:underline">Gérer les sous-catégories →</Link>
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">

              <div>
                <Label>Prix (€) <span className="text-red-500">*</span></Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                  placeholder="45"
                  className={errors.price ? 'border-red-500' : ''}
                  data-testid="input-service-price"
                />
                {errors.price && <p className="text-red-500 text-xs mt-1">{errors.price}</p>}
              </div>
            </div>

            <div>
              <Label>Durée (minutes)</Label>
              <Input
                type="number"
                min="0"
                value={formData.duration_minutes}
                onChange={(e) => setFormData(prev => ({ ...prev, duration_minutes: e.target.value }))}
                placeholder="60"
                data-testid="input-service-duration"
              />
            </div>

            {/* Photo Section */}
            <div className="p-4 bg-[var(--champagne)]/20 border border-[var(--champagne)]">
              <Label className="mb-3 block font-medium">Photo</Label>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Switch
                    checked={formData.no_photo}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, no_photo: checked, image_url: checked ? '' : prev.image_url }))}
                    data-testid="switch-no-photo"
                  />
                  <Label className="cursor-pointer text-sm">Afficher le logo du site au lieu d'une photo</Label>
                </div>
                {!formData.no_photo && (
                  <div>
                    <Label className="text-sm text-[var(--charcoal)]/60">Uploader une image</Label>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelection}
                      className="mt-1"
                      data-testid="input-image-file"
                    />
                    <p className="text-xs text-[var(--charcoal)]/50 mt-1">
                      Formats acceptés : JPG, PNG, WEBP. Un recadrage 5:4 est proposé avant l'upload.
                    </p>
                    <ImageCropUploadDialog
                      inline
                      open={Boolean(pendingImageFile)}
                      onOpenChange={(open) => {
                        if (!open) {
                          setPendingImageFile(null);
                        }
                      }}
                      file={pendingImageFile}
                      onConfirm={handleCroppedImageConfirm}
                      title="Recadrer la photo de prestation"
                      description="Cadrez l'image avant l'upload pour garder des cartes homogènes sur les pages Onglerie, Pédicure et Bien-être."
                    />
                    <div className="mt-3">
                      <Label className="text-sm text-[var(--charcoal)]/60">URL de l'image (optionnel)</Label>
                      <Input
                        value={formData.image_url}
                        onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                        placeholder="https://exemple.com/image.jpg"
                        data-testid="input-image-url"
                      />
                    </div>
                    {isUploadingImage && (
                      <p className="text-xs text-[var(--charcoal)]/60 mt-2 flex items-center gap-2">
                        <Loader2 size={12} className="animate-spin" />
                        Upload en cours...
                      </p>
                    )}
                    {formData.image_url && (
                      <div className="mt-3 h-32 w-full border border-[var(--champagne)] bg-white flex items-center justify-center overflow-hidden">
                        <img
                          src={toAbsoluteUrl(formData.image_url)}
                          alt="Aperçu"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Promo Section */}
            <div className="p-4 bg-green-50 border border-green-200">
              <div className="flex items-center gap-3 mb-3">
                <Switch
                  checked={formData.is_promo}
                  onCheckedChange={(checked) => setFormData(prev => ({ 
                    ...prev, 
                    is_promo: checked,
                    promo_type: checked ? prev.promo_type : '',
                    promo_value: checked ? prev.promo_value : ''
                  }))}
                  data-testid="switch-is-promo"
                />
                <Label className="cursor-pointer font-medium flex items-center gap-2">
                  <Tag size={16} className="text-green-600" />
                  Prestation en promotion
                </Label>
              </div>
              
              {formData.is_promo && (
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <Label>Type de réduction <span className="text-red-500">*</span></Label>
                    <Select
                      value={formData.promo_type}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, promo_type: value }))}
                    >
                      <SelectTrigger className={errors.promo_type ? 'border-red-500' : ''} data-testid="select-promo-type">
                        <SelectValue placeholder="Choisir" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="amount">Montant fixe (€)</SelectItem>
                        <SelectItem value="percent">Pourcentage (%)</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.promo_type && <p className="text-red-500 text-xs mt-1">{errors.promo_type}</p>}
                  </div>
                  <div>
                    <Label>Valeur <span className="text-red-500">*</span></Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max={formData.promo_type === 'percent' ? 100 : undefined}
                      value={formData.promo_value}
                      onChange={(e) => setFormData(prev => ({ ...prev, promo_value: e.target.value }))}
                      placeholder={formData.promo_type === 'percent' ? '20' : '10'}
                      className={errors.promo_value ? 'border-red-500' : ''}
                      data-testid="input-promo-value"
                    />
                    {errors.promo_value && <p className="text-red-500 text-xs mt-1">{errors.promo_value}</p>}
                  </div>
                  {formData.price && formData.promo_value && formData.promo_type && (
                    <div className="col-span-2 p-3 bg-white border border-green-300 text-sm">
                      <span className="text-[var(--charcoal)]/60">Prix après réduction : </span>
                      <span className="font-medium text-green-600">
                        {calculatePromoPrice(parseFloat(formData.price), formData.promo_type, formData.promo_value)?.toFixed(2)}€
                      </span>
                      <span className="text-[var(--charcoal)]/40 line-through ml-2">{formData.price}€</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Active Toggle */}
            <div className="flex items-center gap-3">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                data-testid="switch-service-active"
              />
              <Label>Prestation active (visible sur le site)</Label>
            </div>

            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                className="rounded-none"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-[var(--gold)] text-white hover:bg-[var(--dark-gold)] rounded-none"
                data-testid="btn-save-service"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin mr-2" />
                    Enregistrement...
                  </>
                ) : (
                  editingService ? 'Mettre à jour' : 'Créer'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </PageTransition>
  );
}
